// src/services/userService.js
import { 
  deleteDoc,
  doc, 
  getDoc, 
  getDocs,
  setDoc, 
  serverTimestamp,
  updateDoc, 
  collection,
  query,
  where
} from 'firebase/firestore';
import { auth, db } from '../config/firebase';

const USERS_COLLECTION = 'parrotdocs-users'; // Corrected collection name
const USER_EMAIL_INDEX_COLLECTION = 'parrotdocs-user-emails';
const normalizeEmail = (email) => `${email || ''}`.trim().toLowerCase();
const toEmailIndexId = (email) => normalizeEmail(email).replace(/\./g, ',');

const ensureFirestoreAuthReady = async (expectedUserId = null) => {
  const currentUser = auth.currentUser;
  if (!currentUser) return;
  await currentUser.getIdToken();
  if (expectedUserId && currentUser.uid !== expectedUserId) {
    throw new Error('Authenticated user does not match the requested profile.');
  }
};

export const userService = {
  // Create user profile in Firestore from Firebase Auth user
  async createUserProfile(user, additionalData = {}) {
    if (!user) return;

    try {
      await user.getIdToken();
      const userRef = doc(db, USERS_COLLECTION, user.uid);
      const userDoc = await getDoc(userRef);

      // Only create if user doesn't exist
      const normalizedEmail = normalizeEmail(user.email);
      const emailIndexRef = normalizedEmail ? doc(db, USER_EMAIL_INDEX_COLLECTION, toEmailIndexId(normalizedEmail)) : null;

      if (!userDoc.exists()) {
        const userData = {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName || '',
          photoURL: user.photoURL || '',
          emailVerified: user.emailVerified,
          phoneNumber: user.phoneNumber || '',
          provider: this.getAuthProvider(user),
          providerData: user.providerData || [],
          metadata: {
            creationTime: user.metadata?.creationTime || null,
            lastSignInTime: user.metadata?.lastSignInTime || null
          },
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          lastLoginAt: serverTimestamp(),
          preferences: {
            theme: 'light',
            language: 'en',
            defaultView: 'grid',
            autoSave: true,
            compressionLevel: 'medium',
            notifications: {
              email: true,
              push: false,
              documentSharing: true,
              systemUpdates: true
            }
          },
          subscription: {
            plan: 'free',
            storageUsed: 0,
            documentsCount: 0,
            maxStorage: 1024 * 1024 * 1024, // 1GB for free plan
            maxDocuments: 100
          },
          approvedOrganizationIds: [],
          activeOrganizationId: '',
          profile: {
            bio: '',
            organization: '',
            organizationType: '',
            institutionType: '',
            organizationName: '',
            position: '',
            organizationEmail: '',
            website: '',
            location: '',
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
          },
          ...additionalData
        };

        await setDoc(userRef, userData);
        if (emailIndexRef) {
          await setDoc(emailIndexRef, {
            userId: user.uid,
            email: normalizedEmail,
            displayName: user.displayName || '',
            updatedAt: serverTimestamp()
          }, { merge: true });
        }
        console.log('User profile created successfully in parrotdocs-users');
        return userData;
      } else {
        // Update existing user with latest auth data
        const existingData = userDoc.data();
        const updateData = {
          email: user.email,
          displayName: user.displayName || existingData.displayName,
          photoURL: user.photoURL || existingData.photoURL,
          emailVerified: user.emailVerified,
          phoneNumber: user.phoneNumber || existingData.phoneNumber,
          lastLoginAt: serverTimestamp(),
          metadata: {
            creationTime: user.metadata?.creationTime || existingData.metadata?.creationTime,
            lastSignInTime: user.metadata?.lastSignInTime || new Date()
          },
          updatedAt: serverTimestamp()
        };

        await updateDoc(userRef, updateData);
        if (emailIndexRef) {
          await setDoc(emailIndexRef, {
            userId: user.uid,
            email: normalizedEmail,
            displayName: user.displayName || existingData.displayName || '',
            updatedAt: serverTimestamp()
          }, { merge: true });
        }
        console.log('User profile updated with latest auth data');
        return { ...existingData, ...updateData };
      }
    } catch (error) {
      console.error('Error creating/updating user profile:', error);
      throw error;
    }
  },

  // Get authentication provider from user object
  getAuthProvider(user) {
    if (!user.providerData || user.providerData.length === 0) {
      return 'email';
    }
    
    const provider = user.providerData[0].providerId;
    switch (provider) {
      case 'google.com':
        return 'google';
      case 'facebook.com':
        return 'facebook';
      case 'twitter.com':
        return 'twitter';
      case 'github.com':
        return 'github';
      case 'apple.com':
        return 'apple';
      default:
        return 'email';
    }
  },

  // Get user profile
  async getUserProfile(userId) {
    try {
      await ensureFirestoreAuthReady(userId);
      const userRef = doc(db, USERS_COLLECTION, userId);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        return { id: userDoc.id, ...userDoc.data() };
      } else {
        console.log('No user profile found');
        return null;
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
      throw error;
    }
  },

  // Update user profile
  async updateUserProfile(userId, updates) {
    try {
      await ensureFirestoreAuthReady(userId);
      const userRef = doc(db, USERS_COLLECTION, userId);
      const updateData = {
        ...updates,
        updatedAt: serverTimestamp()
      };
      
      await updateDoc(userRef, updateData);
      console.log('User profile updated successfully');
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw error;
    }
  },

  // Update user preferences
  async updateUserPreferences(userId, preferences) {
    try {
      const userRef = doc(db, USERS_COLLECTION, userId);
      await updateDoc(userRef, {
        preferences: preferences,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating user preferences:', error);
      throw error;
    }
  },

  // Update profile information
  async updateProfileInfo(userId, profileData) {
    try {
      const userRef = doc(db, USERS_COLLECTION, userId);
      await updateDoc(userRef, {
        displayName: profileData.displayName,
        'profile.bio': profileData.bio || '',
        'profile.organization': profileData.organizationName || profileData.organization || '',
        'profile.organizationType': profileData.organizationType || '',
        'profile.institutionType': profileData.organizationType === 'Institution' ? profileData.institutionType || '' : '',
        'profile.organizationName': profileData.organizationName || '',
        'profile.position': profileData.position || '',
        'profile.organizationEmail': profileData.organizationEmail || '',
        'profile.website': profileData.website || '',
        'profile.location': profileData.location || '',
        'profile.timezone': profileData.timezone || '',
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating profile info:', error);
      throw error;
    }
  },

  // Update storage usage
  async updateStorageUsage(userId, storageUsed, documentsCount) {
    try {
      const userRef = doc(db, USERS_COLLECTION, userId);
      await updateDoc(userRef, {
        'subscription.storageUsed': storageUsed,
        'subscription.documentsCount': documentsCount,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating storage usage:', error);
      throw error;
    }
  },

  // Check if email exists
  async checkEmailExists(email) {
    try {
      const usersRef = collection(db, USERS_COLLECTION);
      const q = query(usersRef, where('email', '==', email));
      const querySnapshot = await getDocs(q);
      
      return !querySnapshot.empty;
    } catch (error) {
      console.error('Error checking email:', error);
      throw error;
    }
  },

  async findUsersByEmails(emails = []) {
    try {
      const normalizedEmails = Array.from(new Set(
        (Array.isArray(emails) ? emails : [])
          .map((email) => `${email || ''}`.trim().toLowerCase())
          .filter(Boolean)
      ));

      if (!normalizedEmails.length) return [];

      const snapshots = await Promise.all(
        normalizedEmails.map(async (email) => {
          const emailIndexSnap = await getDoc(doc(db, USER_EMAIL_INDEX_COLLECTION, toEmailIndexId(email)));
          if (!emailIndexSnap.exists()) return null;
          const emailIndex = emailIndexSnap.data();
          if (!emailIndex?.userId) return null;
          const userSnap = await getDoc(doc(db, USERS_COLLECTION, emailIndex.userId));
          if (!userSnap.exists()) return null;
          return {
            id: userSnap.id,
            ...userSnap.data()
          };
        })
      );

      return snapshots.filter(Boolean);
    } catch (error) {
      console.error('Error finding users by emails:', error);
      throw error;
    }
  },

  // Delete user profile
  async deleteUserProfile(userId) {
    try {
      const userRef = doc(db, USERS_COLLECTION, userId);
      await deleteDoc(userRef);
      console.log('User profile deleted successfully');
    } catch (error) {
      console.error('Error deleting user profile:', error);
      throw error;
    }
  }
};
